import { createContext, useContext, useState, useCallback, ReactNode } from 'react'
import { useForm, UseFormReturn } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import * as z from 'zod'
import type { Product, ProductStatus, MediaItem, ProductMedia, ProductOption, ProductVariant, CustomInputDefinition } from '@/types/database'
import { addDays, format } from 'date-fns'
import { getYouTubeThumbnail } from '@/hooks/use-image-upload'

// Convert an ISO timestamp into a local yyyy-MM-dd date input value.
// This preserves the admin-selected calendar day across timezone boundaries.
function toLocalDateInputValue(dateValue: string | null | undefined): string {
  if (!dateValue) return ''
  const parsed = new Date(dateValue)
  if (Number.isNaN(parsed.getTime())) return ''
  return format(parsed, 'yyyy-MM-dd')
}

// Helper to detect if a URL is a video (YouTube/Vimeo)
function isVideoUrl(url: string): boolean {
  if (!url) return false
  const lower = url.toLowerCase()
  return (
    lower.includes('youtube.com') ||
    lower.includes('youtu.be') ||
    lower.includes('vimeo.com')
  )
}

// Helper to initialize media array from product (new schema or legacy)
function initializeMediaFromProduct(product: Product | undefined): MediaItem[] {
  if (!product) return []

  // Try new media array first (cast through unknown for Json type)
  const mediaArray = product.media as unknown as ProductMedia | undefined
  if (Array.isArray(mediaArray) && mediaArray.length > 0) {
    // Validate each item has required properties
    return mediaArray.filter(item =>
      item && typeof item === 'object' && 'type' in item && 'url' in item
    )
  }

  // Fall back to legacy fields
  const items: MediaItem[] = []

  // Add video if exists
  if (product.video_url) {
    items.push({
      type: 'video',
      url: product.video_url,
      thumbnail_url: product.video_thumbnail_url || getYouTubeThumbnail(product.video_url) || undefined,
    })
  }

  // Add images from product_images array
  const productImages = Array.isArray(product.product_images)
    ? (product.product_images as string[])
    : []

  productImages.forEach(url => {
    if (url && typeof url === 'string') {
      items.push({ type: 'image', url })
    }
  })

  return items
}

// Form validation schema
export const productSchema = z.object({
  // Basic info
  title: z.string().min(1, 'Title is required'),
  slug: z.string().min(1, 'Slug is required').regex(/^[a-z0-9-]+$/, 'Slug must be lowercase with hyphens only'),
  description: z.string().optional(),
  short_description: z.string().max(500, 'Short description must be 500 characters or less').optional(),

  // Media
  video_url: z.string().url('Must be a valid URL').optional().or(z.literal('')),
  video_thumbnail_url: z.string().url('Must be a valid URL').optional().or(z.literal('')),
  hero_image_url: z.string().url('Must be a valid URL').optional().or(z.literal('')),
  affiliate_link: z.string().url('Must be a valid URL').optional().or(z.literal('')),

  // Pricing
  original_price: z.number().positive('Price must be positive'),
  featured_discount_percent: z.number().min(0).max(100),
  archive_discount_percent: z.number().min(0).max(100),

  // Status & Scheduling
  status: z.enum(['draft', 'scheduled', 'featured', 'archived', 'hidden']),
  featured_start_date: z.string().optional().or(z.literal('')),
  featured_end_date: z.string().optional().or(z.literal('')),

  // SKU
  sku: z.string().optional().or(z.literal('')),

  // Manufacturer
  manufacturer_name: z.string().optional().or(z.literal('')),
  manufacturer_email: z.string().email().optional().or(z.literal('')),
  manufacturer_order_url: z.string().url().optional().or(z.literal('')),
  cost_price: z.number().min(0).optional(),
  estimated_ship_days: z.number().min(1).optional(),
})

export type ProductFormValues = z.infer<typeof productSchema>

interface ProductEditorContextValue {
  // Form
  form: UseFormReturn<ProductFormValues>

  // Product data
  product: Product | null
  mode: 'create' | 'edit'

  // Unified media array (images + videos)
  media: MediaItem[]
  setMedia: (media: MediaItem[]) => void
  addMedia: (item: MediaItem) => void
  addMediaFromUrl: (url: string) => void
  removeMedia: (index: number) => void
  reorderMedia: (fromIndex: number, toIndex: number) => void
  toggleReviewVideo: (index: number) => void

  // Legacy compatibility - derived from media array
  productImages: string[]

  keyFeatures: string[]
  setKeyFeatures: (features: string[]) => void
  addFeature: () => void
  removeFeature: (index: number) => void
  updateFeature: (index: number, value: string) => void

  specs: Array<{ key: string; value: string }>
  setSpecs: (specs: Array<{ key: string; value: string }>) => void
  addSpec: () => void
  removeSpec: (index: number) => void
  updateSpec: (index: number, field: 'key' | 'value', value: string) => void

  // Options & Variants
  options: ProductOption[]
  setOptions: (options: ProductOption[]) => void
  variants: ProductVariant[]
  setVariants: (variants: ProductVariant[]) => void

  // Custom Inputs
  customInputs: CustomInputDefinition[]
  setCustomInputs: (inputs: CustomInputDefinition[]) => void

  // SKU auto-generation
  skuUserEdited: boolean
  setSkuUserEdited: (edited: boolean) => void

  // Schedule helpers
  setStartDate: (date: string) => void

  // Validation
  hasRequiredFields: () => boolean
  getValidationErrors: () => string[]
}

const ProductEditorContext = createContext<ProductEditorContextValue | null>(null)

interface ProductEditorProviderProps {
  children: ReactNode
  product?: Product
  mode: 'create' | 'edit'
}

export function ProductEditorProvider({ children, product, mode }: ProductEditorProviderProps) {
  // Unified media array state
  const [media, setMedia] = useState<MediaItem[]>(
    initializeMediaFromProduct(product)
  )

  // Derived productImages for legacy compatibility
  const productImages = media
    .filter(item => item.type === 'image')
    .map(item => item.url)

  const [keyFeatures, setKeyFeatures] = useState<string[]>(
    (product?.key_features as string[]) || []
  )
  const [specs, setSpecs] = useState<Array<{ key: string; value: string }>>(
    Object.entries((product?.specs as Record<string, string>) || {}).map(([key, value]) => ({ key, value }))
  )

  const [options, setOptions] = useState<ProductOption[]>(
    (product?.options as unknown as ProductOption[]) || []
  )
  const [variants, setVariants] = useState<ProductVariant[]>(
    (product?.variants as unknown as ProductVariant[]) || []
  )
  const [customInputs, setCustomInputs] = useState<CustomInputDefinition[]>(
    (product?.custom_inputs as unknown as CustomInputDefinition[]) || []
  )

  // SKU auto-generation tracking
  const [skuUserEdited, setSkuUserEdited] = useState<boolean>(
    mode === 'edit' && !!product?.sku
  )

  // Form setup
  const form = useForm<ProductFormValues>({
    resolver: zodResolver(productSchema),
    defaultValues: {
      title: product?.title || '',
      slug: product?.slug || '',
      description: product?.description || '',
      short_description: product?.short_description || '',
      video_url: product?.video_url || '',
      video_thumbnail_url: product?.video_thumbnail_url || '',
      hero_image_url: product?.hero_image_url || '',
      original_price: product?.original_price || 0,
      featured_discount_percent: product?.featured_discount_percent || 40,
      archive_discount_percent: product?.archive_discount_percent || 10,
      affiliate_link: product?.affiliate_link || '',
      status: (product?.status as ProductStatus) || 'draft',
      featured_start_date: toLocalDateInputValue(product?.featured_start_date),
      featured_end_date: toLocalDateInputValue(product?.featured_end_date),
      sku: product?.sku || '',
      manufacturer_name: product?.manufacturer_name || '',
      manufacturer_email: product?.manufacturer_email || '',
      manufacturer_order_url: product?.manufacturer_order_url || '',
      cost_price: product?.cost_price || undefined,
      estimated_ship_days: product?.estimated_ship_days || 5,
    },
  })

  // Unified media management
  const addMedia = useCallback((item: MediaItem) => {
    setMedia(prev => [...prev, item])
  }, [])

  // Smart URL handler - detects video URLs and creates appropriate media item
  const addMediaFromUrl = useCallback((url: string) => {
    if (!url.trim()) return

    if (isVideoUrl(url)) {
      const thumbnail = getYouTubeThumbnail(url)
      addMedia({
        type: 'video',
        url: url.trim(),
        thumbnail_url: thumbnail || undefined,
      })
    } else {
      addMedia({
        type: 'image',
        url: url.trim(),
      })
    }
  }, [addMedia])

  const removeMedia = useCallback((index: number) => {
    setMedia(prev => prev.filter((_, i) => i !== index))
  }, [])

  const reorderMedia = useCallback((fromIndex: number, toIndex: number) => {
    setMedia(prev => {
      const newMedia = [...prev]
      const [removed] = newMedia.splice(fromIndex, 1)
      newMedia.splice(toIndex, 0, removed)
      return newMedia
    })
  }, [])

  // Toggle review video designation (only one video can be marked as review at a time)
  const toggleReviewVideo = useCallback((index: number) => {
    setMedia(prev => {
      const item = prev[index]
      if (!item || item.type !== 'video') return prev

      const isCurrentlyReview = item.is_review

      return prev.map((mediaItem, i) => {
        if (mediaItem.type !== 'video') return mediaItem
        if (i === index) {
          // Toggle this video's review status
          return { ...mediaItem, is_review: !isCurrentlyReview }
        }
        // Clear is_review from other videos when enabling one
        if (!isCurrentlyReview) {
          return { ...mediaItem, is_review: false }
        }
        return mediaItem
      })
    })
  }, [])

  // Feature management
  const addFeature = useCallback(() => {
    setKeyFeatures(prev => [...prev, ''])
  }, [])

  const removeFeature = useCallback((index: number) => {
    setKeyFeatures(prev => prev.filter((_, i) => i !== index))
  }, [])

  const updateFeature = useCallback((index: number, value: string) => {
    setKeyFeatures(prev => {
      const updated = [...prev]
      updated[index] = value
      return updated
    })
  }, [])

  // Spec management
  const addSpec = useCallback(() => {
    setSpecs(prev => [...prev, { key: '', value: '' }])
  }, [])

  const removeSpec = useCallback((index: number) => {
    setSpecs(prev => prev.filter((_, i) => i !== index))
  }, [])

  const updateSpec = useCallback((index: number, field: 'key' | 'value', value: string) => {
    setSpecs(prev => {
      const updated = [...prev]
      updated[index][field] = value
      return updated
    })
  }, [])

  // Schedule helpers - auto-calculate end date (7 days from start)
  // Uses local date strings (yyyy-MM-dd) - time boundaries are applied during save
  const setStartDate = useCallback((date: string) => {
    form.setValue('featured_start_date', date)
    if (date) {
      // Parse as local date and add 7 days for the default duration
      const startDate = new Date(date + 'T00:00:00')
      const endDate = addDays(startDate, 7)
      form.setValue('featured_end_date', format(endDate, 'yyyy-MM-dd'))
    }
  }, [form])

  // Validation helpers
  const hasRequiredFields = useCallback(() => {
    const values = form.getValues()
    return !!(
      values.title &&
      values.slug &&
      values.original_price > 0
    )
  }, [form])

  const getValidationErrors = useCallback(() => {
    const errors: string[] = []
    const values = form.getValues()

    if (!values.title) errors.push('Title is required')
    if (!values.slug) errors.push('Slug is required')
    if (!values.original_price || values.original_price <= 0) errors.push('Price is required')

    // Check for schedule conflicts if scheduling
    if (values.status === 'scheduled') {
      if (!values.featured_start_date) errors.push('Start date is required for scheduled products')
      if (!values.featured_end_date) errors.push('End date is required for scheduled products')
    }

    return errors
  }, [form])

  const value: ProductEditorContextValue = {
    form,
    product: product || null,
    mode,
    // Unified media
    media,
    setMedia,
    addMedia,
    addMediaFromUrl,
    removeMedia,
    reorderMedia,
    toggleReviewVideo,
    // Legacy compatibility
    productImages,
    // Features & specs
    keyFeatures,
    setKeyFeatures,
    addFeature,
    removeFeature,
    updateFeature,
    specs,
    setSpecs,
    addSpec,
    removeSpec,
    updateSpec,
    // Options & Variants
    options,
    setOptions,
    variants,
    setVariants,
    // Custom Inputs
    customInputs,
    setCustomInputs,
    // SKU auto-generation
    skuUserEdited,
    setSkuUserEdited,
    setStartDate,
    hasRequiredFields,
    getValidationErrors,
  }

  return (
    <ProductEditorContext.Provider value={value}>
      {children}
    </ProductEditorContext.Provider>
  )
}

export function useProductEditor() {
  const context = useContext(ProductEditorContext)
  if (!context) {
    throw new Error('useProductEditor must be used within a ProductEditorProvider')
  }
  return context
}
